import { useState, useEffect, useRef } from "react";

export const usePersistedTableState = (
  namespace,
  defaultFilters = {},
  defaultTab = "assessments",
) => {
  // Load page number
  const [page, setPage] = useState(() => {
    const saved = sessionStorage.getItem(`${namespace}_page`);
    return saved ? parseInt(saved, 10) : 1;
  });

  // Load text search query
  const [searchQuery, setSearchQuery] = useState(() => {
    return sessionStorage.getItem(`${namespace}_search`) || "";
  });

  // Load custom filters (like school ID, status, etc.)
  const [persistentFilters, setPersistentFilters] = useState(() => {
    const saved = sessionStorage.getItem(`${namespace}_filters`);
    return saved ? JSON.parse(saved) : defaultFilters;
  });

  const [selectedTab, setSelectedTabState] = useState(() => {
    return sessionStorage.getItem(`${namespace}_tab`) || defaultTab;
  });

  // Track state versions to detect true user modifications versus initial loads
  const prevSearchRef = useRef(searchQuery);
  const prevFiltersRef = useRef(JSON.stringify(persistentFilters));
  const prevTabRef = useRef(selectedTab);

  // Sync to sessionStorage when state changes
  useEffect(() => {
    sessionStorage.setItem(`${namespace}_page`, page);
  }, [namespace, page]);

  useEffect(() => {
    sessionStorage.setItem(`${namespace}_search`, searchQuery);
    if (prevSearchRef.current !== searchQuery) {
      setPage(1);
      prevSearchRef.current = searchQuery;
    }
  }, [namespace, searchQuery]);

  useEffect(() => {
    const stringifiedFilters = JSON.stringify(persistentFilters);
    sessionStorage.setItem(
      `${namespace}_filters`,
      JSON.stringify(persistentFilters),
    );
    // Only drop page to 1 if the internal values inside the filter object actually changed
    if (prevFiltersRef.current !== stringifiedFilters) {
      setPage(1);
      prevFiltersRef.current = stringifiedFilters;
    }
  }, [namespace, persistentFilters]);

  // add: sync tab to sessionStorage, reset page on real tab changes
  useEffect(() => {
    sessionStorage.setItem(`${namespace}_tab`, selectedTab);
    if (prevTabRef.current !== selectedTab) {
      setPage(1);
      prevTabRef.current = selectedTab;
    }
  }, [namespace, selectedTab]);

  const updateFilter = (key, value) => {
    setPersistentFilters((prev) => ({ ...prev, [key]: value }));
  };

  const setFilters = (filters) => {
    setPersistentFilters(filters);
  };

  const setSelectedTab = (tab) => {
    setSelectedTabState(tab);
  };

  return {
    page,
    setPage,
    searchQuery,
    setSearchQuery,
    persistentFilters,
    updateFilter,
    setFilters,
    selectedTab,
    setSelectedTab,
  };
};
